import { useEffect, useRef } from 'react';
import type { NumericInput } from '@velocity-exchange/common/format';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { isResettingRef } from './useSyncResetTradeForm';
import {
	buildBaseSizeText,
	buildQuoteSizeText,
} from '../utils/tradeFormSizeText';

/**
 * Hook that ensures the trade form state's quote and base size values are pinned to the max leverage size if the max leverage is selected.
 *
 * Behavior:
 * - If max decreases: Update size to new max (which also keeps the user within bounds).
 * - If max increases a significant amount: Turn off max mode and preserve current size.
 */
export const usePinTradeformSizeIfMaxLeverageSelected = (
	maxQuoteAvailable: number,
	maxBaseAvailable: number,
	baseSizeStep: NumericInput | undefined
) => {
	const set = useVelocityStore((s) => s.set);
	const setTradeFormMaxLeverageSelected = useVelocityStore(
		(s) => s.setTradeFormMaxLeverageSelected
	);
	const maxLeverageSelected = useVelocityStore(
		(s) => s.tradeForm.maxLeverageSelected
	);

	const prevMaxQuoteRef = useRef<number | null>(null);
	const prevMaxBaseRef = useRef<number | null>(null);

	useEffect(() => {
		// Don't process if we're in the middle of a reset or maxLeverage isn't selected.
		if (!maxLeverageSelected || isResettingRef.current) {
			// Reset refs when max leverage is deselected so next selection starts fresh.
			if (!maxLeverageSelected) {
				prevMaxQuoteRef.current = null;
				prevMaxBaseRef.current = null;
			}

			return;
		}

		const prevMaxQuote = prevMaxQuoteRef.current;
		const prevMaxBase = prevMaxBaseRef.current;

		// If this is the first time max lev is selected, set size to max.
		if (prevMaxQuote === null || prevMaxBase === null) {
			set((s) => {
				// @ts-expect-error
				s.tradeForm.quoteSizeStringValue =
					buildQuoteSizeText(maxQuoteAvailable);
				// @ts-expect-error
				s.tradeForm.baseSizeStringValue = buildBaseSizeText(
					maxBaseAvailable,
					baseSizeStep
				);
			});
			prevMaxQuoteRef.current = maxQuoteAvailable;
			prevMaxBaseRef.current = maxBaseAvailable;
			return;
		}

		// If max increased a significant amount, turn off max mode and preserve
		// current size. We use a significant amount threshold because the max
		// available also fluctuates based on price movement.
		const significantAmountThreshold = 0.001;
		if (
			maxQuoteAvailable > prevMaxQuote * (1 + significantAmountThreshold) ||
			maxBaseAvailable > prevMaxBase * (1 + significantAmountThreshold)
		) {
			setTradeFormMaxLeverageSelected(false);

			return;
		}

		set((s) => {
			// @ts-expect-error
			s.tradeForm.quoteSizeStringValue = buildQuoteSizeText(maxQuoteAvailable);
			// @ts-expect-error
			s.tradeForm.baseSizeStringValue = buildBaseSizeText(
				maxBaseAvailable,
				baseSizeStep
			);
		});

		prevMaxQuoteRef.current = maxQuoteAvailable;
		prevMaxBaseRef.current = maxBaseAvailable;
	}, [
		maxLeverageSelected,
		maxQuoteAvailable,
		maxBaseAvailable,
		baseSizeStep,
		set,
		setTradeFormMaxLeverageSelected,
	]);
};
